import loadable from '@loadable/component'
import { useLocation, navigate } from '@reach/router'
import React, { useState } from 'react'

import { MaintenanceStore } from './components/sections/MaintenanceStore'
import { RestrictMode } from './components/sections/RestrictMode/RestrictMode'
import { SearchOverlay } from './components/ui/SearchOverlay/SearchOverlay'
import { clubHeaderPaths } from './constants'
import { CardContextProvider } from './contexts/CardContext'
import { ClubContextProvider } from './contexts/ClubContext'
import { CmsLandingPageProvider } from './contexts/CmsLandingPage'
import { FreeShippingRulerProvider } from './contexts/FreeShippingRulerContext'
import { MultiplePageContextProvider } from './contexts/MultipleSearchContext'
import { SavingsContextProvider } from './contexts/SavingsContext'
import { SearchOverlayContextProvider } from './contexts/SearchOverlayContext'
import { StoreContextProvider } from './contexts/StoreContext'
import { verifyMaintenance } from '../utils/redirect/maintenance'

import type { StoreContextProps } from './contexts/StoreContext'

import '@plurix/ecom-components/dist/styles/carousel.global.css'

const CommonHeaderRender = loadable(
  () => import('./components/common/Renders/CommonHeaderRender')
)

const CommonModalRender = loadable(
  () => import('./components/common/Renders/CommonModalRender')
)

const CommonFooterRender = loadable(
  () => import('./components/common/Renders/CommonFooterRender')
)

const CommonDesktopNavigationRender = loadable(
  () => import('./components/common/Renders/CommonDesktopNavigationRender')
)

function Layout({
  account,
  secureSubdomain,
  checkoutUrl,
  loginUrl,
  accountUrl,
  salesChannels,
  chatUrl,
  devFeatureToggle,
  featureToggle,
  logo,
  allCategories,
  menuFirst,
  menuSecond,
  departments,
  socialMediaLinks,
  storeInfos,
  pricesAndHours,
  cookiesText,
  loyaltyClubTexts,
  loyaltyClubBanner,
  loyaltyClubDownloadImages,
  loyaltyMosaicList,
  footerPaymentMethods,
  plpBanners,
  children,
  freeShippingBaseValue,
  notificationsBar,
  downloadModal,
  salesWhatsappOffers,
  utmPromotionBond,
  maintenanceStore,
  restrictedMode,
  sections,
  foodRestrictionSection,
  storageRestrictionSection,
  scratchPageContent,
}: StoreContextProps) {
  const [developmentMode, setDevelopmentMode] = useState(false)
  const location = useLocation()
  const isClubHeader = clubHeaderPaths.some((path) =>
    location.pathname.includes(path)
  )

  if (!verifyMaintenance(location.pathname)) {
    let urlIFood = ''
    if (account === 'avenidab2c') {
      urlIFood =
        'https://www.ifood.com.br/delivery/assis-sp/supermercados-avenida-loja-20-vila-tenis-clube/078bbf11-fdab-4712-a6d2-03c0e6be69b4'
    }
    if (account === 'superpao') {
      urlIFood =
        'https://www.ifood.com.br/delivery/guarapuava-pr/superpao-guarapuava---loja-03---saldanha-santa-cruz/3cb443b4-9091-4c29-a466-8caf0c096e22'
    }
    if (account === 'compremais') {
      urlIFood =
        'https://www.ifood.com.br/delivery/guarapuava-pr/compre-mais--mega-loja-guarapuava---loja-21---boqueirao-boqueirao/aa1d96ec-bb65-4104-84fb-39e4cdd77537'
    }
    navigate(urlIFood)

    return <div>Redirecionando para o iFood</div>
  }

  if (
    process.env.NODE_ENV === 'production' &&
    !developmentMode &&
    account === 'smrholding'
  ) {
    return (
      <RestrictMode
        passKey={restrictedMode?.passKey}
        setDevelopmentMode={setDevelopmentMode}
      />
    )
  }

  if (maintenanceStore?.isActive) {
    return (
      <MaintenanceStore
        isActive={maintenanceStore?.isActive}
        logo={maintenanceStore?.logo}
        maintenanceImage={maintenanceStore?.maintenanceImage}
      />
    )
  }

  return (
    <div id="layout">
      <StoreContextProvider
        // store infos
        account={account}
        secureSubdomain={secureSubdomain}
        checkoutUrl={checkoutUrl}
        loginUrl={loginUrl}
        accountUrl={accountUrl}
        salesChannels={salesChannels}
        chatUrl={chatUrl}
        devFeatureToggle={devFeatureToggle}
        // cms
        featureToggle={featureToggle}
        logo={logo}
        allCategories={allCategories}
        footerPaymentMethods={footerPaymentMethods}
        menuFirst={menuFirst}
        menuSecond={menuSecond}
        departments={departments}
        socialMediaLinks={socialMediaLinks}
        storeInfos={storeInfos}
        pricesAndHours={pricesAndHours}
        cookiesText={cookiesText}
        loyaltyClubTexts={loyaltyClubTexts}
        loyaltyClubBanner={loyaltyClubBanner}
        loyaltyClubDownloadImages={loyaltyClubDownloadImages}
        loyaltyMosaicList={loyaltyMosaicList}
        plpBanners={plpBanners}
        freeShippingBaseValue={freeShippingBaseValue}
        notificationsBar={notificationsBar}
        downloadModal={downloadModal}
        salesWhatsappOffers={salesWhatsappOffers}
        utmPromotionBond={utmPromotionBond}
        maintenanceStore={maintenanceStore}
        sections={sections}
        foodRestrictionSection={foodRestrictionSection}
        storageRestrictionSection={storageRestrictionSection}
        scratchPageContent={scratchPageContent}
      >
        <FreeShippingRulerProvider>
          <ClubContextProvider>
            <CardContextProvider>
              <SavingsContextProvider>
                <MultiplePageContextProvider>
                  <SearchOverlayContextProvider>
                    <CmsLandingPageProvider>
                      <CommonHeaderRender isClubHeader={isClubHeader} />
                      <main>
                        <SearchOverlay />
                        {!isClubHeader && <CommonDesktopNavigationRender />}
                        {children}
                        <CommonFooterRender />
                        <CommonModalRender />
                      </main>
                    </CmsLandingPageProvider>
                  </SearchOverlayContextProvider>
                </MultiplePageContextProvider>
              </SavingsContextProvider>
            </CardContextProvider>
          </ClubContextProvider>
        </FreeShippingRulerProvider>
      </StoreContextProvider>
    </div>
  )
}

export default Layout
